import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Siren,
  ArrowLeft,
  Search,
  BookOpen,
  Filter,
  X,
  Info,
  ShieldAlert,
  FileText,
  ListChecks,
} from "lucide-react";
import PnjLogo from "@/components/PnjLogo";
import data from "@/data/emergency_guidance.json";

/**
 * EmergencyGuidance — Panduan Penanganan saat Gangguan dan Darurat.
 * Sumber: SOP-ROP/TO-02 Rev.05 (panduan_penanganan_gangguan_MRTJ.csv)
 * 56 entries × 11 kategori.
 *
 * Alur: user mengetik jenis gangguan → daftar hasil terfilter →
 * detail muncul dengan prosedur penanganan (numbered steps) & pasal rujukan.
 * Tersedia filter kategori.
 */
const ENTRIES = data.entries || [];
const CATEGORIES = Array.from(
  new Set(ENTRIES.map((e) => e.kategori).filter(Boolean)),
).sort();

const highlight = (text, needle) => {
  if (!needle || !text) return text;
  const idx = text.toLowerCase().indexOf(needle.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-[#dc2626]/40 text-white rounded px-0.5">
        {text.slice(idx, idx + needle.length)}
      </span>
      {text.slice(idx + needle.length)}
    </>
  );
};

// Split "1. step\n2. step" into an ordered list. Falls back to plain text
// if the pattern isn't recognized.
const parseSteps = (text) => {
  if (!text) return [];
  // Split by lines that start with a number followed by "." or ")"
  const parts = text
    .split(/\n(?=\s*\d+[.)]\s*)/)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.map((p) => p.replace(/^\d+[.)]\s*/, ""));
};

const DetailPanel = ({ entry }) => {
  if (!entry) return null;
  const steps = parseSteps(entry.prosedur);
  return (
    <div
      data-testid="detail-panel"
      className="rounded-2xl border border-[#dc2626]/40 bg-gradient-to-br from-[#0d243a] to-[#062036] p-6 shadow-lg shadow-red-500/10"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#fbbf24]">
            {entry.kategori}
          </div>
          <h3 className="mt-1 font-display text-2xl font-bold">
            {entry.jenis}
          </h3>
        </div>
        {entry.pasal && (
          <div className="inline-flex items-center gap-2 rounded-lg border border-[#fbbf24]/40 bg-[#f59e0b]/10 px-3 py-1.5">
            <FileText className="h-4 w-4 text-[#fbbf24]" />
            <span className="text-xs font-mono text-[#fbbf24]">
              {entry.pasal}
            </span>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-[#22c55e]/30 bg-[#062036] p-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#22c55e] mb-3">
          <ListChecks className="h-4 w-4" />
          Prosedur Penanganan
        </div>
        {steps.length > 1 ? (
          <ol className="list-decimal list-outside pl-5 space-y-2 text-sm text-slate-100 leading-relaxed">
            {steps.map((s, i) => (
              <li key={i} className="pl-1">
                {s}
              </li>
            ))}
          </ol>
        ) : (
          <div className="text-sm text-slate-100 leading-relaxed whitespace-pre-line">
            {entry.prosedur || "—"}
          </div>
        )}
      </div>
    </div>
  );
};

export default function EmergencyGuidance() {
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [selected, setSelected] = useState(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = ENTRIES;
    if (categoryFilter !== "ALL") {
      list = list.filter((e) => e.kategori === categoryFilter);
    }
    if (!q) return list;
    const scored = list
      .map((e) => {
        const jenis = (e.jenis || "").toLowerCase();
        const proc = (e.prosedur || "").toLowerCase();
        const kat = (e.kategori || "").toLowerCase();
        let score = 0;
        if (jenis === q) score = 100;
        else if (jenis.startsWith(q)) score = 80;
        else if (jenis.includes(q)) score = 60;
        else if (kat.includes(q)) score = 40;
        else if (proc.includes(q)) score = 20;
        return { e, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);
    return scored.map((x) => x.e);
  }, [query, categoryFilter]);

  const perfectMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return ENTRIES.find((e) => (e.jenis || "").toLowerCase() === q) || null;
  }, [query]);

  const active = selected || perfectMatch;

  return (
    <div
      className="min-h-screen bg-[#0c1b2a] text-white relative overflow-hidden"
      data-testid="page-emergency-guidance"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-25 eg-grid" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(220,38,38,0.14),transparent_55%)]" />
      </div>

      <header className="relative z-10 max-w-6xl mx-auto flex items-center justify-between px-6 pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Menu Utama
        </Link>
        <PnjLogo size={32} />
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-8 pb-16">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-[#dc2626] to-[#f97316] flex items-center justify-center shadow-lg shadow-red-500/30">
            <Siren className="h-8 w-8 text-[#0c1b2a]" strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-[#fbbf24]">
              Panduan Gangguan & Darurat · {ENTRIES.length} entries
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold leading-tight">
              Panduan Penanganan saat<br className="hidden md:block" />{" "}
              Gangguan dan Darurat
            </h1>
          </div>
        </div>

        {/* Source reference banner */}
        <div
          className="mb-8 rounded-xl border border-[#fbbf24]/40 bg-[#f59e0b]/10 px-4 py-3 flex items-start gap-3"
          data-testid="source-banner"
        >
          <FileText className="h-5 w-5 text-[#fbbf24] shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="text-slate-400">Sumber database: </span>
            <span className="font-semibold text-[#fbbf24] font-mono">
              SOP-ROP/TO-02 Rev.05
            </span>
          </div>
        </div>

        {/* Search + filter */}
        <div className="rounded-2xl border border-[#243a52] bg-[#0b1a29]/70 p-4 md:p-5">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelected(null);
                }}
                placeholder="Ketik jenis gangguan (mis. rem darurat, banjir, kebakaran, ATP)..."
                data-testid="eg-search-input"
                className="w-full pl-10 pr-10 py-3 rounded-lg bg-[#0c1b2a] border border-[#243a52] focus:border-[#dc2626] focus:outline-none focus:ring-1 focus:ring-[#dc2626] text-sm placeholder:text-slate-500"
                autoFocus
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#243a52]"
                  aria-label="Clear"
                  data-testid="eg-clear"
                >
                  <X className="h-4 w-4 text-slate-400" />
                </button>
              )}
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                data-testid="eg-category-filter"
                className="w-full md:w-72 pl-10 pr-4 py-3 rounded-lg bg-[#0c1b2a] border border-[#243a52] focus:border-[#dc2626] focus:outline-none text-sm appearance-none cursor-pointer"
              >
                <option value="ALL">Semua kategori</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Quick category chips */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button
              onClick={() => setCategoryFilter("ALL")}
              className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded border transition-colors ${
                categoryFilter === "ALL"
                  ? "bg-[#dc2626]/20 text-[#fbbf24] border-[#fbbf24]/40"
                  : "bg-[#122032] text-slate-400 border-transparent hover:text-white"
              }`}
              data-testid="eg-chip-ALL"
            >
              Semua ({ENTRIES.length})
            </button>
            {CATEGORIES.map((c) => {
              const count = ENTRIES.filter((e) => e.kategori === c).length;
              return (
                <button
                  key={c}
                  onClick={() => setCategoryFilter(c)}
                  className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded border transition-colors ${
                    categoryFilter === c
                      ? "bg-[#dc2626]/20 text-[#fbbf24] border-[#fbbf24]/40"
                      : "bg-[#122032] text-slate-400 border-transparent hover:text-white"
                  }`}
                  data-testid={`eg-chip-${c}`}
                >
                  {c} ({count})
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
            <Info className="h-3.5 w-3.5" />
            <span>
              Ketik jenis gangguan → prosedur penanganan otomatis muncul di
              bawah.
            </span>
          </div>
        </div>

        {active && (
          <div className="mt-6">
            <DetailPanel entry={active} />
          </div>
        )}

        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">
            {query.trim()
              ? `Hasil pencarian (${results.length})`
              : categoryFilter === "ALL"
                ? `Daftar gangguan (${results.length})`
                : `${categoryFilter} (${results.length})`}
          </div>

          {results.length === 0 && (
            <div
              className="rounded-xl border border-dashed border-[#CA2047]/40 bg-[#CA2047]/5 p-6 flex items-start gap-3"
              data-testid="eg-empty"
            >
              <ShieldAlert className="h-5 w-5 text-[#ff8fa3] shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-[#ff8fa3]">
                  Jenis gangguan tidak ditemukan
                </div>
                <div className="text-sm text-slate-300">
                  Coba ubah kata kunci atau ganti filter kategori.
                </div>
              </div>
            </div>
          )}

          <div className="grid gap-2" data-testid="eg-results">
            {results.map((e, idx) => {
              const isActive =
                active &&
                active.jenis === e.jenis &&
                active.kategori === e.kategori;
              return (
                <button
                  key={`${e.kategori}-${e.jenis}-${idx}`}
                  onClick={() => setSelected(e)}
                  data-testid={`eg-result-${idx}`}
                  className={`text-left rounded-xl border p-4 transition-all hover:-translate-y-0.5 ${
                    isActive
                      ? "border-[#dc2626]/70 bg-[#062036] shadow-md shadow-red-500/10"
                      : "border-[#243a52] bg-[#0b1a29]/70 hover:border-[#dc2626]/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#122032] text-[#fbbf24]">
                          {e.kategori}
                        </span>
                        {e.pasal && (
                          <span className="text-[10px] font-mono text-slate-500">
                            {e.pasal}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-slate-100 font-semibold">
                        {highlight(e.jenis, query)}
                      </div>
                      {e.prosedur && (
                        <div className="mt-1 text-xs text-slate-500 line-clamp-2">
                          {highlight(e.prosedur.slice(0, 180) + (e.prosedur.length > 180 ? "..." : ""), query)}
                        </div>
                      )}
                    </div>
                    <BookOpen className="h-4 w-4 text-slate-500 mt-1 shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <footer className="mt-16 pt-6 border-t border-[#243a52] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-slate-500">
          <div>
            <span className="text-slate-400 font-display font-semibold">
              CRLS Console Suite
            </span>{" "}
            · Panduan Gangguan & Darurat · v1.0
          </div>
          <PnjLogo size={32} />
        </footer>
      </main>

      <style>{`
        .eg-grid {
          background-image:
            linear-gradient(rgba(220,38,38,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(220,38,38,0.05) 1px, transparent 1px);
          background-size: 42px 42px;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
